import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Campaign as CampaignIcon,
  Add as AddIcon,
  PersonOff as PersonOffIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  listCampaigns,
  createCampaign,
  approveCampaign,
  startCampaign,
  uploadLeadsFile,
  attachExistingLeads,
  getPendingManualSend,
  markLeadAsSent,
  markLeadAsLost,
  Campaign,
  LeadUploadResponse,
  ManualSendPendingItem,
  ManualDispatchScheduleEntry,
} from '../services/campaignManualService';

const PAGE_LIMIT = 50;
const DAILY_WARNING_THRESHOLD = 50;

const getTodayKey = () => `wa_manual_sent_${new Date().toISOString().slice(0, 10)}`;

const CampanhaManual: React.FC = () => {
  // Campanhas
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | ''>('');

  // Nova campanha
  const [showNewCampaignForm, setShowNewCampaignForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTemplate, setNewTemplate] = useState(
    'Olá {{nome}}! Aqui é da MedPrime Benefícios. Tudo bem?'
  );
  const [newWhatsappTemplateId, setNewWhatsappTemplateId] = useState('');
  const [newWhatsappTemplateLanguage, setNewWhatsappTemplateLanguage] = useState('pt_BR');
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [approvingOrStarting, setApprovingOrStarting] = useState(false);
  const [scheduleEntries, setScheduleEntries] = useState<{ hora: string; quantidade: string }[]>([]);

  // Upload
  const [leadSource, setLeadSource] = useState<'upload' | 'existing'>('upload');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [audienceName, setAudienceName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attachingExisting, setAttachingExisting] = useState(false);
  const [uploadResult, setUploadResult] = useState<LeadUploadResponse | null>(null);

  // Fila de disparo manual
  const [pendingItems, setPendingItems] = useState<ManualSendPendingItem[]>([]);
  const [totalPending, setTotalPending] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loadingPending, setLoadingPending] = useState(false);
  const [markingLeadId, setMarkingLeadId] = useState<number | null>(null);
  const [markingLostLeadId, setMarkingLostLeadId] = useState<number | null>(null);
  const [availableNow, setAvailableNow] = useState<number | null>(null);

  // Contador diário
  const [dailyCount, setDailyCount] = useState<number>(() => {
    const stored = localStorage.getItem(getTodayKey());
    return stored ? parseInt(stored, 10) : 0;
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId) || null;

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const data = await listCampaigns();
      setCampaigns(data);
    } catch (error) {
      showSnackbar('Erro ao carregar campanhas', 'error');
    }
    setLoadingCampaigns(false);
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const fetchPending = useCallback(
    async (campaignId: number, skipValue: number, append: boolean) => {
      setLoadingPending(true);
      try {
        const data = await getPendingManualSend(campaignId, skipValue, PAGE_LIMIT);
        setTotalPending(data.total_pending);
        setAvailableNow(data.available_now ?? null);
        setPendingItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setSkip(skipValue);
      } catch (error) {
        showSnackbar('Erro ao carregar fila de disparo manual', 'error');
      }
      setLoadingPending(false);
    },
    []
  );

  useEffect(() => {
    if (selectedCampaignId !== '') {
      fetchPending(selectedCampaignId as number, 0, false);
      setUploadResult(null);
    } else {
      setPendingItems([]);
      setTotalPending(0);
      setAvailableNow(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId]);

  const handleSelectCampaign = (e: SelectChangeEvent<number | string>) => {
    const value = e.target.value;
    setSelectedCampaignId(value === '' ? '' : Number(value));
  };

  const HORA_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

  const addScheduleEntry = () => {
    setScheduleEntries((prev) => [...prev, { hora: '', quantidade: '' }]);
  };

  const updateScheduleEntry = (idx: number, field: 'hora' | 'quantidade', value: string) => {
    setScheduleEntries((prev) => prev.map((entry, i) => (i === idx ? { ...entry, [field]: value } : entry)));
  };

  const removeScheduleEntry = (idx: number) => {
    setScheduleEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreateCampaign = async () => {
    if (!newName.trim()) {
      showSnackbar('Informe um nome para a campanha', 'warning');
      return;
    }

    const validSchedule: ManualDispatchScheduleEntry[] = scheduleEntries
      .filter((e) => HORA_REGEX.test(e.hora) && parseInt(e.quantidade, 10) > 0)
      .map((e) => ({ hora: e.hora, quantidade: parseInt(e.quantidade, 10) }))
      .sort((a, b) => a.hora.localeCompare(b.hora));

    if (scheduleEntries.length > 0 && validSchedule.length !== scheduleEntries.length) {
      showSnackbar('Verifique os horários (HH:MM) e quantidades do agendamento antes de criar a campanha', 'warning');
      return;
    }

    setCreatingCampaign(true);
    try {
      const created = await createCampaign({
        name: newName.trim(),
        message_template: newTemplate,
        whatsapp_template_id: newWhatsappTemplateId.trim() || undefined,
        whatsapp_template_language: newWhatsappTemplateId.trim() ? newWhatsappTemplateLanguage : undefined,
        manual_dispatch_schedule: validSchedule.length > 0 ? validSchedule : undefined,
      });
      showSnackbar('Campanha criada com sucesso!', 'success');
      setShowNewCampaignForm(false);
      setNewName('');
      setNewWhatsappTemplateId('');
      setNewWhatsappTemplateLanguage('pt_BR');
      setScheduleEntries([]);
      await fetchCampaigns();
      setSelectedCampaignId(created.id);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao criar campanha';
      showSnackbar(message, 'error');
    }
    setCreatingCampaign(false);
  };

  const handleApproveCampaign = async () => {
    if (!selectedCampaign) return;
    setApprovingOrStarting(true);
    try {
      await approveCampaign(selectedCampaign.id);
      showSnackbar('Campanha aprovada com sucesso!', 'success');
      await fetchCampaigns();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao aprovar campanha';
      showSnackbar(message, 'error');
    }
    setApprovingOrStarting(false);
  };

  const handleStartCampaign = async () => {
    if (!selectedCampaign) return;
    setApprovingOrStarting(true);
    try {
      await startCampaign(selectedCampaign.id);
      showSnackbar('Campanha iniciada com sucesso!', 'success');
      await fetchCampaigns();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao iniciar campanha';
      showSnackbar(message, 'error');
    }
    setApprovingOrStarting(false);
  };

  const handleUpload = async () => {
    if (!selectedCampaign) {
      showSnackbar('Selecione ou crie uma campanha antes de enviar a planilha', 'warning');
      return;
    }
    if (!uploadFile) {
      showSnackbar('Selecione um arquivo (.xlsx, .xls ou .csv)', 'warning');
      return;
    }
    if (!audienceName.trim()) {
      showSnackbar('Informe um nome para a audiência', 'warning');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await uploadLeadsFile(selectedCampaign.id, uploadFile, audienceName.trim());
      setUploadResult(result);
      showSnackbar('Planilha processada com sucesso!', 'success');
      await fetchCampaigns();
      await fetchPending(selectedCampaign.id, 0, false);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao processar a planilha';
      showSnackbar(message, 'error');
    }
    setUploading(false);
  };

  const handleAttachExisting = async () => {
    if (!selectedCampaign) {
      showSnackbar('Selecione ou crie uma campanha antes de anexar os leads existentes', 'warning');
      return;
    }
    setAttachingExisting(true);
    setUploadResult(null);
    try {
      const result = await attachExistingLeads(selectedCampaign.id, audienceName.trim() || undefined, 'novo');
      setUploadResult(result);
      showSnackbar(`${result.valid_rows} leads existentes (status Novo) anexados à campanha!`, 'success');
      await fetchCampaigns();
      await fetchPending(selectedCampaign.id, 0, false);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao anexar leads existentes';
      showSnackbar(message, 'error');
    }
    setAttachingExisting(false);
  };

  const incrementDailyCount = () => {
    const key = getTodayKey();
    const current = parseInt(localStorage.getItem(key) || '0', 10);
    const next = current + 1;
    localStorage.setItem(key, String(next));
    setDailyCount(next);
  };

  const openWhatsAppForLead = (item: ManualSendPendingItem) => {
    const formattedNumber = item.telefone.replace(/\D/g, '');
    const message = encodeURIComponent(item.message_preview);

    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.location.href = `whatsapp://send?phone=${formattedNumber}&text=${message}`;
    } else {
      window.open(item.whatsapp_link, '_blank');
    }
  };

  const handleMarkAsSent = async (item: ManualSendPendingItem) => {
    if (!selectedCampaign) return;
    setMarkingLeadId(item.lead_id);
    try {
      const result = await markLeadAsSent(selectedCampaign.id, item.lead_id);
      setPendingItems((prev) => prev.filter((p) => p.lead_id !== item.lead_id));
      setTotalPending((prev) =>
        typeof result.remaining_pending === 'number' ? result.remaining_pending : Math.max(0, prev - 1)
      );
      if (!result.already_sent) {
        incrementDailyCount();
      }
      showSnackbar(
        result.already_sent ? 'Este lead já havia sido marcado como enviado' : 'Lead marcado como enviado!',
        'success'
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao marcar lead como enviado';
      showSnackbar(message, 'error');
    }
    setMarkingLeadId(null);
  };

  const handleMarkAsLost = async (item: ManualSendPendingItem) => {
    if (!selectedCampaign) return;
    setMarkingLostLeadId(item.lead_id);
    try {
      const result = await markLeadAsLost(selectedCampaign.id, item.lead_id);
      setPendingItems((prev) => prev.filter((p) => p.lead_id !== item.lead_id));
      setTotalPending((prev) =>
        typeof result.remaining_pending === 'number' ? result.remaining_pending : Math.max(0, prev - 1)
      );
      showSnackbar('Lead marcado como sem WhatsApp (Perdido)', 'info');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao marcar lead como sem WhatsApp';
      showSnackbar(message, 'error');
    }
    setMarkingLostLeadId(null);
  };

  const handleLoadMore = () => {
    if (selectedCampaignId === '') return;
    fetchPending(selectedCampaignId as number, skip + PAGE_LIMIT, true);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'draft':
        return <Chip label="Rascunho" color="default" size="small" />;
      case 'approved':
        return <Chip label="Aprovada" color="info" size="small" />;
      case 'active':
        return <Chip label="Ativa" color="success" size="small" />;
      case 'paused':
        return <Chip label="Pausada" color="warning" size="small" />;
      case 'completed':
        return <Chip label="Concluída" color="secondary" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Disparo de WhatsApp (Manual)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Importe sua planilha de leads e dispare mensagens manualmente pelo seu WhatsApp
        </Typography>
      </Box>

      {/* Aviso fixo: fluxo gratuito */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Este fluxo <strong>NÃO</strong> usa a API paga da Meta — as mensagens são enviadas pelo seu
        WhatsApp pessoal/Web, sem custo. Cada envio exige uma ação manual sua (clicar em "Abrir
        WhatsApp" e enviar).
      </Alert>

      {/* Seção 1: Campanha */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Campanha
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <FormControl fullWidth size="small">
              <InputLabel id="campaign-select-label">Selecionar campanha existente</InputLabel>
              <Select
                labelId="campaign-select-label"
                label="Selecionar campanha existente"
                value={selectedCampaignId}
                onChange={handleSelectCampaign}
                disabled={loadingCampaigns}
              >
                <MenuItem value="">
                  <em>Nenhuma selecionada</em>
                </MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} ({c.total_leads} leads, {c.messages_sent} enviados)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowNewCampaignForm((prev) => !prev)}
              fullWidth
            >
              Nova campanha
            </Button>
          </Grid>
        </Grid>

        {showNewCampaignForm && (
          <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <TextField
              label="Nome da campanha"
              fullWidth
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Template da mensagem"
              helperText="Use {{nome}}, {{email}} ou {{telefone}} para personalizar a mensagem automaticamente. {{nome}} envia só o primeiro nome (ex: Vitor)."
              fullWidth
              multiline
              rows={3}
              value={newTemplate}
              onChange={(e) => setNewTemplate(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <DescriptionIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">
                  Template aprovado pela Meta (opcional)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Preencha só se o lead ainda não iniciou conversa nas últimas 24h (disparo frio) --
                a Meta exige um template pré-aprovado nesse caso, texto livre é rejeitado. Deixe
                vazio pra continuar mandando o "Template da mensagem" acima como texto livre.
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  label="Nome do template (Meta)"
                  placeholder="orcamento_pronto"
                  size="small"
                  value={newWhatsappTemplateId}
                  onChange={(e) => setNewWhatsappTemplateId(e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Idioma"
                  placeholder="pt_BR"
                  size="small"
                  value={newWhatsappTemplateLanguage}
                  onChange={(e) => setNewWhatsappTemplateLanguage(e.target.value)}
                  disabled={!newWhatsappTemplateId.trim()}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">
                  Agendamento de cota (opcional)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Libera uma quantidade de leads na fila em cada horário do dia (fuso Brasília). Cota não
                usada num horário soma no seguinte. Deixe vazio pra liberar tudo de uma vez.
              </Typography>
              {scheduleEntries.map((entry, idx) => (
                <Box key={idx} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Horário"
                    placeholder="07:00"
                    size="small"
                    value={entry.hora}
                    onChange={(e) => updateScheduleEntry(idx, 'hora', e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    label="Quantidade"
                    type="number"
                    size="small"
                    value={entry.quantidade}
                    onChange={(e) => updateScheduleEntry(idx, 'quantidade', e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <Tooltip title="Remover horário">
                    <IconButton size="small" onClick={() => removeScheduleEntry(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={addScheduleEntry}>
                Adicionar horário
              </Button>
            </Box>

            <Button
              variant="contained"
              onClick={handleCreateCampaign}
              disabled={creatingCampaign}
            >
              {creatingCampaign ? <CircularProgress size={20} /> : 'Criar campanha'}
            </Button>
          </Box>
        )}

        {selectedCampaign && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CampaignIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    {selectedCampaign.name}
                  </Typography>
                  {getStatusChip(selectedCampaign.status)}
                </Box>
                <Box display="flex" gap={1}>
                  {selectedCampaign.status === 'draft' && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleApproveCampaign}
                      disabled={approvingOrStarting}
                    >
                      Aprovar campanha
                    </Button>
                  )}
                  {selectedCampaign.status === 'approved' && (
                    <Tooltip title="É preciso enviar a planilha de leads (seção 2) antes de iniciar">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={handleStartCampaign}
                          disabled={approvingOrStarting || selectedCampaign.total_leads === 0}
                        >
                          Iniciar campanha
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Leads
                  </Typography>
                  <Typography variant="h6">{selectedCampaign.total_leads}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Enviados
                  </Typography>
                  <Typography variant="h6">{selectedCampaign.messages_sent}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Conversões
                  </Typography>
                  <Typography variant="h6">{selectedCampaign.conversions}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Paper>

      {/* Seção 2: Origem dos leads */}
      <Paper sx={{ p: 3, mb: 3, opacity: selectedCampaign ? 1 : 0.5 }}>
        <Typography variant="h6" gutterBottom>
          2. Adicionar leads à campanha
        </Typography>
        {!selectedCampaign && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Selecione ou crie uma campanha na seção acima antes de adicionar leads.
          </Alert>
        )}

        <ToggleButtonGroup
          value={leadSource}
          exclusive
          onChange={(_e, value) => value && setLeadSource(value)}
          size="small"
          disabled={!selectedCampaign}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="upload">Importar planilha</ToggleButton>
          <ToggleButton value="existing">Usar leads existentes (status Novo)</ToggleButton>
        </ToggleButtonGroup>

        {leadSource === 'upload' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                disabled={!selectedCampaign}
              >
                {uploadFile ? uploadFile.name : 'Escolher planilha (.xlsx, .xls, .csv)'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setUploadFile(e.target.files ? e.target.files[0] : null)}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Nome da audiência"
                size="small"
                fullWidth
                value={audienceName}
                onChange={(e) => setAudienceName(e.target.value)}
                disabled={!selectedCampaign}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleUpload}
                disabled={!selectedCampaign || uploading}
              >
                {uploading ? <CircularProgress size={20} /> : 'Enviar planilha'}
              </Button>
            </Grid>
          </Grid>
        )}

        {leadSource === 'existing' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Anexa a esta campanha todos os leads que já estão na base com status <strong>Novo</strong> —
                não reimporta a planilha, então não corre risco de duplicar os leads que você já subiu antes.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Nome da audiência (opcional)"
                size="small"
                fullWidth
                value={audienceName}
                onChange={(e) => setAudienceName(e.target.value)}
                disabled={!selectedCampaign}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAttachExisting}
                disabled={!selectedCampaign || attachingExisting}
              >
                {attachingExisting ? <CircularProgress size={20} /> : 'Anexar leads existentes (Novo)'}
              </Button>
            </Grid>
          </Grid>
        )}

        {uploadResult && (
          <Box sx={{ mt: 3 }}>
            <Alert severity={uploadResult.invalid_rows > 0 ? 'warning' : 'success'}>
              Processamento concluído: <strong>{uploadResult.total_rows}</strong> linhas lidas,{' '}
              <strong>{uploadResult.valid_rows}</strong> válidas, <strong>{uploadResult.duplicates}</strong>{' '}
              duplicadas e <strong>{uploadResult.invalid_rows}</strong> inválidas.
            </Alert>
            {uploadResult.validation_errors && uploadResult.validation_errors.length > 0 && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Primeiros erros de validação
                  </Typography>
                  <List dense>
                    {uploadResult.validation_errors.slice(0, 5).map((err, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={`Linha ${err.row}: ${err.error}`}
                          secondary={err.data ? JSON.stringify(err.data) : undefined}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Paper>

      {/* Seção 3: Fila de disparo manual */}
      <Paper sx={{ p: 3, mb: 3, opacity: selectedCampaign ? 1 : 0.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h6">
            3. Fila de disparo manual {selectedCampaign ? `(${totalPending} pendentes)` : ''}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Total de mensagens marcadas como enviadas hoje">
              <Chip
                icon={<CheckCircleIcon />}
                label={`Hoje: ${dailyCount}`}
                color={dailyCount > DAILY_WARNING_THRESHOLD ? 'warning' : 'default'}
              />
            </Tooltip>
          </Box>
        </Box>

        {dailyCount > DAILY_WARNING_THRESHOLD && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Você já enviou {dailyCount} mensagens hoje — considere pausar para reduzir o risco de
            bloqueio do seu WhatsApp. O ritmo seguro sugerido é de ~40-60 mensagens/dia por número
            pessoal.
          </Alert>
        )}

        {!selectedCampaign && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Selecione uma campanha para ver a fila de disparo.
          </Alert>
        )}

        {selectedCampaign && availableNow !== null && (
          <Alert severity={availableNow > 0 ? 'info' : 'warning'} sx={{ mt: 2 }}>
            Agendamento ativo: <strong>{availableNow}</strong> de {totalPending} leads pendentes
            liberados agora (fuso Brasília). {availableNow === 0 && 'Aguarde o próximo horário do agendamento.'}
          </Alert>
        )}

        {selectedCampaign && (
          <>
            <List sx={{ mt: 2 }}>
              {pendingItems.map((item, idx) => (
                <React.Fragment key={item.lead_id}>
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <Box display="flex" gap={1}>
                        <Tooltip title="Abrir WhatsApp">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => openWhatsAppForLead(item)}
                          >
                            Abrir WhatsApp
                          </Button>
                        </Tooltip>
                        <Tooltip title="Marcar como enviado">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                markingLeadId === item.lead_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CheckCircleIcon />
                                )
                              }
                              onClick={() => handleMarkAsSent(item)}
                              disabled={markingLeadId === item.lead_id}
                            >
                              Marcar como enviado
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Número não tem WhatsApp (marca lead como Perdido)">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={
                                markingLostLeadId === item.lead_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <PersonOffIcon />
                                )
                              }
                              onClick={() => handleMarkAsLost(item)}
                              disabled={markingLostLeadId === item.lead_id}
                            >
                              Sem WhatsApp
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {item.nome} — {item.telefone}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ pr: 28 }}>
                          {item.message_preview}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < pendingItems.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
              {pendingItems.length === 0 && !loadingPending && (
                <Box textAlign="center" p={3}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhum lead pendente de disparo
                  </Typography>
                </Box>
              )}
              {loadingPending && (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </List>

            {pendingItems.length < totalPending && !loadingPending && (
              <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={handleLoadMore}>
                  Carregar mais
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CampanhaManual;
